import type {
  MeetingMentorFeedbackDto,
  MeetingMyStudentsOverview,
  MeetingMyStudentsOverviewMeetingDto,
  MeetingMyStudentsStudent,
  MeetingMyStudentsStudentDto,
  MeetingStudentReportDto,
  MeetingSubjectRatingDto,
  RatingsOverviewDto,
  RatingsOverviewMentorDto,
  RatingsOverviewRatingDto,
  RatingsOverviewReportDto,
  RatingsOverviewStudentDto,
} from "meetings/student-meeting/types";
import {
  meetingFromDto,
  studentFromDto,
} from "./studentMeetingMyStudents.mapper";

/**
 * Adapts the legacy school-wide ratings-overview payload
 * (GET /api/school/{schoolId}/ratings-overview) to the canonical my-students
 * DTOs, so the "Overzichten > Besprekingen" screen can render the same student
 * block as the my-students / mentor-class / lesson-class pages.
 *
 * Everything here maps legacy snake_case -> canonical snake_case only; the
 * camelCase domain mapping stays in studentMeetingMyStudents.mapper.ts so there
 * is exactly one definition of the domain shape.
 */

/** Only 1-4 count as a given rating; the legacy payload casts NULL to 0 and
 * uses 5 for "not rated" (@see MeetingSubjectRatingResource). */
const GIVEN_RATINGS = [1, 2, 3, 4];

const toGivenRating = (raw: number | null | undefined): number | null => {
  if (raw === null || raw === undefined) {
    return null;
  }

  return GIVEN_RATINGS.includes(Number(raw)) ? Number(raw) : null;
};

/** The legacy payload mixes ints, bools and nulls for its flag columns. */
const toBool = (raw: number | boolean | null | undefined): boolean =>
  raw === true || Number(raw) === 1;

/**
 * The legacy payload passes `meeting_timeframe_settings.share_feedback_at`
 * straight through (raw "Y-m-d H:i:s", or `""` when unset), while the canonical
 * payload preformats it as the legacy display string "d-m-Y H:i" — which is
 * what MeetingStudentShareFeedback parses.
 */
const toDutchDateTime = (raw: string | null | undefined): string | null => {
  if (!raw) {
    return null;
  }

  const parts = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(raw);

  if (parts === null) {
    // Already in the "d-m-Y H:i" display format.
    return raw;
  }

  const [, year, month, day, hour, minute] = parts;

  return `${day}-${month}-${year} ${hour}:${minute}`;
};

const fullName = (parts: Array<string | null | undefined>): string =>
  parts.filter(Boolean).join(" ");

const mentorName = (mentor: RatingsOverviewMentorDto): string =>
  mentor.name ??
  fullName([mentor.first_name, mentor.middle_name, mentor.last_name]);

const ratingToDto = (
  rating: RatingsOverviewRatingDto,
): MeetingSubjectRatingDto => ({
  id: rating.id,
  subject: rating.subject_shortage,
  teacher_shortage: rating.teacher_shortage,
  lesson_class: rating.lesson_class ?? "",
  rating: toGivenRating(rating.rating),
  feedback: rating.rating_remark ?? null,
  feedforward: rating.rating_action ?? null,
  // meetings.allow_grades is the authoritative grade gate
  // (registry: MEETING-GRADES-001 in meeting.invariants.md).
  grades: toBool(rating.meeting.allow_grades)
    ? (rating.grades ?? []).map((grade) => ({
        type: grade.type as MeetingSubjectRatingDto["grades"][number]["type"],
        grade: grade.grade,
      }))
    : [],
  allow_student_ratings: toBool(rating.allow_student_ratings),
  // Passed through raw, exactly as the canonical resource does — a 5 or null
  // simply lights up none of the traffic-light icons.
  student_rating: rating.student_rating ?? null,
});

const reportToDto = (
  report: RatingsOverviewReportDto | null | undefined,
): MeetingStudentReportDto => ({
  invite: toBool(report?.invite),
  feedback_seen: toBool(report?.feedback_seen),
  // The legacy payload sets feedback_sent from `invite`; keep that meaning.
  feedback_sent: toBool(report?.feedback_sent ?? report?.invite),
});

/**
 * The overview payload carries mentors and report summaries separately, so the
 * per-mentor comment rows are joined here on mentor id (falling back to the
 * shortage, as the canonical service does). `written_at` is null because the
 * overview payload carries no summary timestamp.
 */
const mentorFeedbackToDto = (
  student: RatingsOverviewStudentDto,
  currentTeacherId: number,
): MeetingMentorFeedbackDto[] => {
  const summaries = student.report?.report_summaries ?? [];

  return (student.mentors ?? []).map((mentor) => {
    const row = summaries.find(
      (summary) =>
        (summary.mentor_user_id !== null &&
          summary.mentor_user_id === mentor.id) ||
        (summary.teacher_shortage !== null &&
          summary.teacher_shortage === mentor.shortage),
    );

    return {
      shortage: mentor.shortage ?? "",
      name: mentorName(mentor),
      profile_image: mentor.profile_image ?? null,
      summary: row?.summary ?? "",
      written_at: null,
      can_edit: mentor.id === currentTeacherId,
    };
  });
};

const studentToDto = (
  student: RatingsOverviewStudentDto,
  meetingName: string,
  currentTeacherId: number,
): MeetingMyStudentsStudentDto => {
  const name = fullName([
    student.first_name,
    student.middle_name,
    student.last_name,
  ]);

  return {
    id: student.id,
    first_name: student.first_name,
    middle_name: student.middle_name,
    last_name: student.last_name,
    formatted_name: name,
    full_name: name,
    mentor_class: student.mentor_class,
    profile_image: student.profile_image,
    mentors: (student.mentors ?? []).map((mentor) => ({
      id: mentor.id,
      name: mentorName(mentor),
      shortage: mentor.shortage,
    })),
    ratings: student.ratings.map(ratingToDto),
    // Not in the legacy payload, and nothing reads it: the traffic-light icons
    // come from the RATING_LEVELS frontend constant.
    rating_options: [],
    meeting_name: meetingName,
    // The card derives its progress bar from `ratings`, not from this.
    progress: [],
    report: reportToDto(student.report),
    mentor_feedback: mentorFeedbackToDto(student, currentTeacherId),
  };
};

const meetingToDto = (
  dto: RatingsOverviewDto,
  fallbackMeetingId: number,
): MeetingMyStudentsOverviewMeetingDto => {
  const meeting = dto.students[0]?.ratings[0]?.meeting;

  return {
    id: meeting?.id ?? fallbackMeetingId,
    name: dto.name,
    status: dto.status,
    meeting_type: "meeting",
    // The overview payload only holds the current page of students, so a
    // meeting-wide progress figure cannot be derived from it. Only the
    // my-students page header reads these; the student card does not.
    total_ratings: 0,
    total_given_ratings: 0,
    closed_modify_at: null,
    rating_options: [],
    question_prompt: null,
    // RatingGradeBadges reads the school-level grade settings from the grade
    // store rather than from this payload.
    grade_settings: { enable_se_grade: false, enable_rapport_grade: false },
    mail_student: Number(meeting?.mail_student ?? 0),
    share_feedback_at: toDutchDateTime(meeting?.share_feedback_at),
  };
};

export const ratingsOverviewStudentFromDto = (
  student: RatingsOverviewStudentDto,
  meetingName: string,
  currentTeacherId: number,
): MeetingMyStudentsStudent =>
  studentFromDto(studentToDto(student, meetingName, currentTeacherId));

export const ratingsOverviewMeetingFromDto = (
  dto: RatingsOverviewDto,
  fallbackMeetingId: number,
): MeetingMyStudentsOverview["meeting"] =>
  meetingFromDto(meetingToDto(dto, fallbackMeetingId));
